import React, { useEffect } from "react";
import { setCurrentPage } from "../store/navSlice";
import emailjs from "@emailjs/browser";
import {
  ArrowForward,
  Chat,
  CheckCircle,
  Email,
  LocationOn,
  Phone,
  Verified,
  SupportAgent,
  CorporateFare,
  Security,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Fade,
  Drawer,
  List,
  AppBar,
  Toolbar,
  ListItem,
  ListItemText,
  keyframes,
  Grid,
  Grow,
  useMediaQuery,
  Paper,
  Snackbar,
  TextField,
  Typography,
  useTheme,
  Zoom,
  IconButton,
  Stack,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import LogoImage from "../images/decodia-logo.png";
import { useSelector, useDispatch } from "react-redux";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import CloseIcon from "@mui/icons-material/Close";
import ConsultationFormDialog from "./Consultation";

const styles = `
@keyframes gradientShift {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes float0 {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
    opacity: 0.5;
  }
  33% {
    transform: translateY(-30px) translateX(30px) rotate(120deg);
    opacity: 0.8;
  }
  66% {
    transform: translateY(20px) translateX(-20px) rotate(240deg);
    opacity: 0.6;
  }
}

@keyframes float1 {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
    opacity: 0.4;
  }
  25% {
    transform: translateY(-40px) translateX(-40px) rotate(90deg);
    opacity: 0.7;
  }
  50% {
    transform: translateY(10px) translateX(40px) rotate(180deg);
    opacity: 0.5;
  }
  75% {
    transform: translateY(-20px) translateX(20px) rotate(270deg);
    opacity: 0.6;
  }
}

@keyframes float2 {
  0%, 100% {
    transform: translateY(0) scale(1);
    opacity: 0.3;
  }
  50% {
    transform: translateY(-50px) scale(1.2);
    opacity: 0.6;
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.3;
  }
  50% {
    transform: scale(1.5);
    opacity: 0.6;
  }
}

@keyframes floatParticle {
  0% {
    transform: translateY(100vh) translateX(0) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 0.7;
  }
  90% {
    opacity: 0.7;
  }
  100% {
    transform: translateY(-100px) translateX(100px) rotate(360deg);
    opacity: 0;
  }
}

@keyframes shimmer {
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
}

@keyframes orbit {
  0% {
    transform: rotate(0deg) translateX(250px) rotate(0deg);
  }
  100% {
    transform: rotate(360deg) translateX(250px) rotate(-360deg);
  }
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-30px);
  }
}

@keyframes ripple {
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(4);
    opacity: 0;
  }
}

@keyframes glow {
  0%, 100% {
    opacity: 0.8;
    filter: blur(20px);
  }
  50% {
    opacity: 0.6;
    filter: blur(30px);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes moveBg {
  0% {
    background-position: 0% 0%;
  }
  100% {
    background-position: 100% 100%;
  }
}

@keyframes gridMove {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 50px 50px;
  }
}
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideDown = keyframes`
  from { 
    opacity: 0;
    transform: translateY(-10px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseAnimation = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
`;

const underline = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

const shimmerAnimation = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [openForm, setOpenForm] = useState(false);

  const { menuItems } = useSelector((state) => state.navigation);

  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const currentPath = location.pathname;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavigation = (path, label) => {
    navigate(path);
    dispatch(setCurrentPage(label));
    setDrawerOpen(false);
  };

  const desktopMenu = (
    <Box
      sx={{
        display: "flex",
        gap: 4,
        alignItems: "center",
        animation: `${fadeIn} 0.6s ease-out`,
      }}
    >
      {menuItems.map((item, index) => (
        <Box
          key={item.id}
          sx={{
            animation: `${slideDown} 0.4s ease-out ${index * 0.05}s forwards`,
            opacity: 0,
          }}
        >
          <Button
            onClick={() => handleNavigation(item.path, item.label)}
            sx={{
              color: "white",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.95rem",
              position: "relative",
              padding: "8px 12px",
              minWidth: "auto",
              borderRadius: "4px",
              transition: "all 0.3s ease",

              "&:hover": {
                color: "#EED688",
                background:
                  "linear-gradient(135deg, rgba(255, 107, 53, 0.1), rgba(238, 214, 136, 0.1))",
                transform: "translateY(-2px)",

                "&::after": {
                  content: '""',
                  position: "absolute",
                  width: "100%",
                  height: "2px",
                  bottom: "0",
                  left: "0",
                  background:
                    "linear-gradient(90deg, #FF6B35, #FF8E53, #EED688)",
                  animation: `${underline} 0.3s ease-out`,
                  borderRadius: "2px",
                },
              },

              ...(currentPath === item.path && {
                color: "#FF6B35",
                fontWeight: 700,
                background:
                  "linear-gradient(135deg, rgba(255, 107, 53, 0.15), rgba(255, 142, 83, 0.1))",

                "&::after": {
                  content: '""',
                  position: "absolute",
                  width: "100%",
                  height: "2px",
                  bottom: "0",
                  left: "0",
                  background:
                    "linear-gradient(90deg, #FF6B35, #FF8E53, #FF6B35)",
                  borderRadius: "2px",
                  animation: `${pulseAnimation} 2s infinite`,
                },
              }),
            }}
          >
            {item.label}
          </Button>
        </Box>
      ))}

      <Box
        sx={{
          animation: `${slideDown} 0.4s ease-out 0.3s forwards`,
          opacity: 0,
          ml: 1,
        }}
      >
        <Button
          variant="contained"
          onClick={() => setOpenForm(true)}
          sx={{
            background: "linear-gradient(45deg, #FF6B35 0%, #FF8E53 100%)",
            backgroundSize: "200% 200%",
            color: "white",
            fontWeight: 700,
            borderRadius: "8px",
            padding: "10px 24px",
            fontSize: "0.95rem",
            textTransform: "none",
            position: "relative",
            overflow: "hidden",
            animation: `${pulseAnimation} 3s infinite`,
            transition: "all 0.3s ease",

            "&:hover": {
              background: "linear-gradient(45deg, #E55A2B 0%, #FF7B42 100%)",
              transform: "translateY(-2px)",
              boxShadow: "0 8px 20px rgba(255, 107, 53, 0.3)",
              animation: `${pulseAnimation} 1.5s infinite`,

              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background:
                  "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
                animation: `${shimmerAnimation} 1.5s ease-out`,
              },
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ChatBubbleIcon sx={{ fontSize: 18 }} />
            Free Consultation
          </Box>
        </Button>

        <ConsultationFormDialog
          open={openForm}
          onClose={() => setOpenForm(false)}
        />
      </Box>
    </Box>
  );

  const mobileMenu = (
    <>
      <IconButton
        onClick={() => setDrawerOpen(true)}
        sx={{
          color: "white",
          animation: `${fadeIn} 0.5s ease-out`,
          transition: "transform 0.3s ease",

          "&:hover": {
            transform: "rotate(90deg)",
          },
        }}
      >
        <MenuIcon />
      </IconButton>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: 280,
            background: "linear-gradient(135deg, #0F0F0F 0%, #1a1a1a 100%)",
            color: "white",
            animation: `${fadeIn} 0.3s ease-out`,
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <img
              src={LogoImage}
              alt="DECODIA"
              style={{ height: 50, width: "auto" }}
            />
            <IconButton
              onClick={() => setDrawerOpen(false)}
              sx={{
                color: "white",
                transition: "transform 0.3s ease",

                "&:hover": {
                  transform: "rotate(90deg)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <List>
            {menuItems.map((item, index) => (
              <ListItem
                key={item.id}
                button
                onClick={() => handleNavigation(item.path, item.label)}
                sx={{
                  py: 2,
                  mb: 1,
                  borderRadius: "4px",
                  backgroundColor:
                    currentPath === item.path
                      ? "rgba(255, 107, 53, 0.1)"
                      : "transparent",
                  animation: `${fadeIn} 0.3s ease-out ${index * 0.05}s forwards`,
                  opacity: 0,
                  transition: "all 0.3s ease",

                  "&:hover": {
                    backgroundColor: "rgba(255, 107, 53, 0.05)",
                    paddingLeft: "20px",
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: 600,
                    color: currentPath === item.path ? "#FF6B35" : "white",
                  }}
                />
              </ListItem>
            ))}
          </List>

          <Box sx={{ mt: 3, p: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() =>
                handleNavigation("/free-consultation", "Free Consultation")
              }
              sx={{
                background: "linear-gradient(45deg, #FF6B35 30%, #FF8E53 90%)",
                color: "white",
                fontWeight: 600,
                borderRadius: "8px",
                padding: "14px 24px",
                animation: `${pulseAnimation} 3s infinite`,
                transition: "all 0.3s ease",

                "&:hover": {
                  background:
                    "linear-gradient(45deg, #E55A2B 30%, #FF7B42 90%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 15px rgba(255, 107, 53, 0.3)",
                  animation: `${pulseAnimation} 1.5s infinite`,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <ChatBubbleIcon sx={{ fontSize: 18 }} />
                Free Consultation
              </Box>
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );

  return (
    <AppBar
      position="absolute"
      elevation={0}
      sx={{
        background: "transparent",
        backdropFilter: scrolled ? "blur(10px)" : "none",
        transition: "all 0.3s ease",
        borderBottom: scrolled ? "1px solid rgba(255,255,255,0.1)" : "none",
        animation: `${fadeIn} 0.6s ease-out`,
      }}
    >
      <Container maxWidth="1400px" sx={{ margin: 0, px: 0, py: 2 }}>
        <Toolbar
          sx={{
            minHeight: 70,
            justifyContent: "space-between",
            width: "100%",
            maxWidth: "1400px",
            margin: "0 auto",
          }}
        >
          <Box
            onClick={() => handleNavigation("/", "Home")}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              transition: "transform 0.3s ease",

              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
          >
            <img
              src={LogoImage}
              alt="DECODIA"
              style={{ height: 60, width: "auto" }}
            />
          </Box>

          {!isMobile ? desktopMenu : mobileMenu}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

const Contact = () => {
  const theme = useTheme();

  // Colors matching your navigation
  const colors = {
    charcoalGrey: "#232933ff",
    charcoalMedium: "#2d3748",
    charcoalLight: "#b2b3b4ff",
    terracotta: "#e53e3e",
    terracottaLight: "#fc8181",
    terracottaDark: "#c53030",
    softOlive: "#295237ff",
    softOliveLight: "#2b342eff",
    softOliveDark: "#3f5246ff",
    warmBeige: "#f7fafc",
    offWhite: "#edf2f7",
    pureWhite: "#ffffff",
    gold: "#a97100ff",
    lightGold: "#755e09ff",
    accentBlue: "#4299e1",
    darkBlue: "#2b6cb0",
  };

  const fonts = {
    header: "'Playfair Display', serif",
    body: "'Montserrat', sans-serif",
    mono: "'JetBrains Mono', monospace",
  };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    department: "",
    company: "",
    projectType: "",
    location: "", // ADDED: Location field
  });

  const [errors, setErrors] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeInfo, setActiveInfo] = useState(0);
  const [copyStatus, setCopyStatus] = useState({ email: false, phone: false });

  const projectTypes = ["Commercial Space Design", "Residential Space Design"];

  // First contact info section
  const classicContactInfo = [
    {
      icon: <LocationOn fontSize="large" />,
      title: "Visit Our Office",
      details: [
        "Decodia P S Abacus - 703",
        "7th Floor , NH12 , Action Area IIE ,",
        "Newtown , West Bengal 700161 , India",
      ],
      color: colors.terracotta,
      bgColor: alpha(colors.terracotta, 0.1),
      delay: 100,
      action: () =>
        window.open(
          "https://www.google.com/maps/place/Decodia+Design+Studio/@22.6191563,88.4589333,19z/data=!3m1!4b1!4m6!3m5!1s0x39f89f824c69a885:0x163982e2b2768dee!8m2!3d22.6191551!4d88.459577!16s%2Fg%2F11z0j7xf5r?entry=ttu&g_ep=EgoyMDI2MDMyOS4wIKXMDSoASAFQAw%3D%3D",
          "_blank",
        ),
    },
    {
      icon: <Email fontSize="large" />,
      title: "Email Us",
      details: [
        "info@decodia.in",
        "decodiaofficial@gmail.com",
        "design@decodia.in",
      ],
      color: colors.terracotta,
      bgColor: alpha(colors.terracotta, 0.1),
      delay: 200,
      action: () => {
        window.location.href =
          "https://mail.google.com/mail/u/0/?tab=rm&ogbl#inbox?compose=CllgCJNqspNwwbmQHznSbNWJhjBXXVVkSTljhBQQRMzjnZqbPrXqnFTtMZJSPkgKBFvCDJpWgZL";
      },
    },
    {
      icon: <Phone fontSize="large" />,
      title: "Call Us",
      details: ["+91 83708 25457", "+91 8100364941", "24/7 Support Available"],
      color: colors.terracotta,
      bgColor: alpha(colors.terracotta, 0.1),
      delay: 300,
      action: () => {
        if (window.confirm("Call DECODIA support at +91 83708 25457?")) {
          window.location.href = "tel:+91 83708 25457";
        }
      },
    },
  ];

  // Second contact info section
  const executiveContactInfo = [
    {
      icon: <CorporateFare fontSize="large" />,
      title: "Corporate Headquarters",
      details: [
        "Decodia P S Abacus - 703, 7th Floor",
        "NH12, Action Area IIE, Newtown",
        "West Bengal 700161",
        "India",
      ],
      color: colors.terracotta,
      bgColor: alpha(colors.terracotta, 0.1),
      delay: 100,
      action: () =>
        window.open(
          "https://www.google.com/maps/place/Decodia+Design+Studio/@22.6191563,88.4589333,19z/data=!3m1!4b1!4m6!3m5!1s0x39f89f824c69a885:0x163982e2b2768dee!8m2!3d22.6191551!4d88.459577!16s%2Fg%2F11z0j7xf5r?entry=ttu&g_ep=EgoyMDI2MDMyOS4wIKXMDSoASAFQAw%3D%3D",
          "_blank",
        ),
      subtitle: "Main Office",
    },
    {
      icon: <SupportAgent fontSize="large" />,
      title: "24/7 Support",
      details: [
        "Technical Support: +91 83708 25457",
        "Client Relations: decodiaofficial@gmail.com",
        "Emergency Hotline Available",
      ],
      color: colors.softOlive,
      bgColor: alpha(colors.softOlive, 0.1),
      delay: 200,
      action: () => {
        if (
          window.confirm("Call DECODIA emergency support at +91 8100364941?")
        ) {
          window.location.href = "tel:+918100364941";
        }
      },
      subtitle: "Always Available",
    },
    {
      icon: <Security fontSize="large" />,
      title: "Secure Communication",
      details: [
        "Encrypted Messaging",
        "NDA Protected",
        "GDPR Compliant",
        "ISO Certified",
      ],
      color: colors.gold,
      bgColor: alpha(colors.gold, 0.1),
      delay: 400,
      action: null,
      subtitle: "Confidential & Safe",
    },
  ];
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Full name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Business email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (
      !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\D/g, ""))
    )
      if (!formData.message.trim()) {
        newErrors.message = "Detailed message is required";
      }
    if (!formData.location.trim()) {
      newErrors.location = "Address is required";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);

    try {
      // EmailJS Configuration - Use your actual credentials
      const SERVICE_ID = "service_a8t9sbt";
      const TEMPLATE_ID = "template_kkd9iri";
      const PUBLIC_KEY = "PinX8mgdGG_GoQx52";

      // Prepare email data with location included
      const templateParams = {
        to_name: "Decodia",
        to_email: "info@decodia.in",
        from_name: formData.name,
        from_email: formData.email,
        from_phone: formData.phone || "Not provided",
        company: formData.company || "Not provided",
        department: formData.department || "General Inquiry",
        project_type: formData.projectType || "Not specified",
        location: formData.location || "Not provided", // ADDED: Location field
        subject:
          formData.subject || `Contact Form Submission from ${formData.name}`,
        message: formData.message,
        date: new Date().toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
        site: "DECODIA Website",
      };

      // Send email using EmailJS
      const response = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        templateParams,
        PUBLIC_KEY,
      );

      // Show success message
      setSubmitting(false);
      setOpenSnackbar(true);

      // Reset form with location included
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        department: "",
        company: "",
        projectType: "",
        location: "", // ADDED: Reset location
      });
    } catch (error) {
      console.error("❌ Failed to send email:", error);
      setSubmitting(false);

      let errorMessage =
        "Please try again later or contact us directly at dipanita.decodia123@gmail.com";

      if (error.text) {
        errorMessage = error.text;
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(`Failed to send message: ${errorMessage}`);

      // Fallback: Open mail client with location included
      const mailtoLink = `mailto:info@decodia.in?subject=DECODIA Contact: ${encodeURIComponent(formData.subject || "New Message")}&body=Name: ${encodeURIComponent(formData.name)}%0D%0AEmail: ${encodeURIComponent(formData.email)}%0D%0APhone: ${encodeURIComponent(formData.phone || "")}%0D%0ACompany: ${encodeURIComponent(formData.company || "")}%0D%0ADepartment: ${encodeURIComponent(formData.department || "")}%0D%0AProject Type: ${encodeURIComponent(formData.projectType || "")}%0D%0ALocation: ${encodeURIComponent(formData.location || "")}%0D%0A%0D%0AMessage:%0D%0A${encodeURIComponent(formData.message)}`;

      if (
        window.confirm("Email service failed. Open your email client instead?")
      ) {
        window.location.href = mailtoLink;
      }
    }
  };

  return (
    <>
      <style>{styles}</style>
      <Box
        sx={{
          minHeight: "100vh",
          position: "relative",
          overflow: "hidden",
          background: "transparent",
          backgroundSize: "400% 400%",
          animation: "gradientShift 2s ease infinite",
        }}
      >
        <Navigation />

        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
            zIndex: 0,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(800px at 20% 20%, rgba(120, 119, 198, 0.15) 0%, transparent 50%),
                radial-gradient(600px at 80% 20%, rgba(255, 167, 196, 0.15) 0%, transparent 50%),
                radial-gradient(700px at 20% 80%, rgba(120, 220, 220, 0.15) 0%, transparent 50%),
                radial-gradient(500px at 80% 80%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)
              `,
              animation: "moveBg 30s ease-in-out infinite alternate",
            },
          }}
        >
          {/* Background elements remain the same */}
          {Array.from({ length: 4 }).map((_, i) => (
            <Box
              key={`blob-${i}`}
              sx={{
                position: "absolute",
                width: 500 + i * 100,
                height: 500 + i * 100,
                borderRadius: "50%",
                background: `radial-gradient(circle at ${30 + i * 15}% ${
                  30 + i * 15
                }%, ${
                  [
                    "rgba(120, 119, 198, 0.1)",
                    "rgba(255, 167, 196, 0.1)",
                    "rgba(120, 220, 220, 0.1)",
                    "rgba(168, 85, 247, 0.1)",
                  ][i]
                } 0%, transparent 70%)`,
                filter: "blur(20px)",
                animation: `float${i % 3} ${30 + i * 10}s ease-in-out infinite`,
                animationDelay: `${i * 1}s`,
                top: `${(i * 25) % 100}%`,
                left: `${(i * 20) % 100}%`,
                opacity: 0.7,
              }}
            />
          ))}
        </Box>

        {/* Dark overlay for hero section */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "700px",
            background: `linear-gradient(135deg, ${alpha(
              colors.charcoalGrey,
              0.9,
            )} 20%, ${alpha(colors.charcoalMedium, 0.9)} 80%)`,
            clipPath: "polygon(0 0, 100% 0, 100% 85%, 0% 100%)",
            zIndex: 1,
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(45deg, ${alpha(
                colors.terracotta,
                0.1,
              )} 0%, ${alpha(colors.softOlive, 0.1)} 100%)`,
              mixBlendMode: "overlay",
            },
          }}
        />

        {/* Hero Section */}
        <Box sx={{ position: "relative", zIndex: 2 }}>
          <Container maxWidth="xl">
            <Box
              sx={{
                pt: { xs: 12, md: 16 },
                pb: { xs: 12, md: 16 },
                textAlign: "center",
                color: colors.pureWhite,
              }}
            >
              <Fade in timeout={800}>
                <Chip
                  label="ENTERPRISE CONTACT"
                  sx={{
                    bgcolor: alpha(colors.pureWhite, 0.15),
                    color: colors.pureWhite,
                    mb: 4,
                    px: 3,
                    py: 1.5,
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    letterSpacing: "1.5px",
                    border: `1px solid ${alpha(colors.pureWhite, 0.2)}`,
                    backdropFilter: "blur(10px)",
                    fontFamily: fonts.body,
                  }}
                  icon={<Verified sx={{ color: colors.softOlive }} />}
                />
              </Fade>

              <Fade in timeout={1000}>
                <Typography
                  variant="h1"
                  sx={{
                    fontFamily: fonts.header,
                    fontWeight: 800,
                    mb: 3,
                    fontSize: {
                      xs: "1.8rem",
                      sm: "2.5rem",
                      md: "3rem",
                      lg: "3.5rem",
                      xl: "4rem",
                    },
                    letterSpacing: { xs: "-0.2px", md: "-0.5px" },
                    lineHeight: { xs: 1.2, md: 1.1 },
                    maxWidth: { xs: "100%", md: "1000px" },
                    mx: "auto",
                    textShadow: `0 4px 20px ${alpha(colors.charcoalGrey, 0.5)}`,
                  }}
                >
                  Elevate Your Vision with{" "}
                  <Box
                    component="span"
                    sx={{
                      background:
                        "linear-gradient(45deg, #FFFFFF 30%, #E53E3E 90%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      animation: "shimmer 8s infinite linear",
                      backgroundSize: "1000px 100%",
                      display: "inline-block",
                    }}
                  >
                    Professional Excellence
                  </Box>
                </Typography>
              </Fade>

              <Fade in timeout={1200}>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: fonts.body,
                    color: alpha(colors.pureWhite, 0.9),
                    fontWeight: 400,
                    maxWidth: "700px",
                    mx: "auto",
                    mb: 6,
                    fontSize: { xs: "1.1rem", md: "1.25rem" },
                    lineHeight: 1.6,
                  }}
                >
                  Connect with our executive team for strategic partnerships,
                  enterprise solutions, and transformative digital experiences.
                </Typography>
              </Fade>

              <Fade in timeout={1400}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Email />}
                    onClick={() =>
                      document
                        .getElementById("contact-form")
                        .scrollIntoView({ behavior: "smooth" })
                    }
                    sx={{
                      bgcolor: colors.terracotta,
                      color: colors.pureWhite,
                      fontFamily: fonts.body,
                      fontWeight: 700,
                      px: 4,
                      py: 1.5,
                      borderRadius: "12px",
                      boxShadow: `0 8px 25px ${alpha(colors.terracotta, 0.4)}`,
                      "&:hover": {
                        bgcolor: colors.terracottaDark,
                        transform: "translateY(-2px)",
                        boxShadow: `0 12px 35px ${alpha(
                          colors.terracotta,
                          0.6,
                        )}`,
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Send Message
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<Phone />}
                    onClick={() => {
                      if (
                        window.confirm(
                          "Call DECODIA executive line at +91 8100364941?",
                        )
                      ) {
                        window.location.href = "tel:+918100364941";
                      }
                    }}
                    sx={{
                      borderColor: alpha(colors.pureWhite, 0.3),
                      color: colors.pureWhite,
                      fontFamily: fonts.body,
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      borderRadius: "12px",
                      "&:hover": {
                        borderColor: colors.pureWhite,
                        bgcolor: alpha(colors.pureWhite, 0.1),
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Book your free consultation
                  </Button>
                </Box>
              </Fade>
            </Box>
          </Container>
        </Box>

        {/* Classic Contact Cards Section */}
        <Container
          maxWidth="lg"
          sx={{ mt: -6, position: "relative", zIndex: 2 }}
        >
          <Grid container spacing={3} justifyContent="center">
            {classicContactInfo.map((info, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Grow in timeout={info.delay}>
                  <Card
                    elevation={4}
                    onClick={info.action}
                    onMouseEnter={() => setActiveInfo(index)}
                    sx={{
                      height: "100%",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      cursor: info.action ? "pointer" : "default",
                      border: `2px solid ${alpha(info.color, 0.1)}`,
                      borderRadius: "20px",
                      overflow: "hidden",
                      bgcolor: colors.pureWhite,
                      position: "relative",
                      "&:hover": {
                        transform: "translateY(-10px)",
                        boxShadow: `0 20px 40px ${alpha(info.color, 0.15)}`,
                        borderColor: info.action ? info.color : "transparent",
                        "& .card-icon": {
                          transform: "scale(1.1) rotate(5deg)",
                        },
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "5px",
                        background: `linear-gradient(90deg, ${
                          info.color
                        }, ${alpha(info.color, 0.7)})`,
                      },
                    }}
                  >
                    <CardContent sx={{ textAlign: "center", p: 3.5 }}>
                      <Box
                        className="card-icon"
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "20px",
                          bgcolor: info.bgColor,
                          color: info.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 24px",
                          transition: "all 0.3s ease",
                          boxShadow: `0 8px 20px ${alpha(info.color, 0.2)}`,
                        }}
                      >
                        {info.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        gutterBottom
                        fontWeight="700"
                        sx={{ color: info.color }}
                      >
                        {info.title}
                      </Typography>
                      {info.details.map((detail, idx) => (
                        <Fade in timeout={500 + idx * 100} key={idx}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            paragraph
                            sx={{
                              mb: 1.5,
                              fontWeight: 500,
                            }}
                          >
                            {detail}
                          </Typography>
                        </Fade>
                      ))}
                      {info.action && (
                        <Fade in timeout={800}>
                          <Box sx={{ mt: 3 }}>
                            <Button
                              variant="outlined"
                              size="medium"
                              onClick={(e) => {
                                e.stopPropagation();
                                info.action();
                              }}
                              sx={{
                                color: info.color,
                                borderColor: alpha(info.color, 0.3),
                                "&:hover": {
                                  backgroundColor: alpha(info.color, 0.1),
                                  borderColor: info.color,
                                  transform: "translateY(-2px)",
                                },
                                transition: "all 0.3s ease",
                                fontWeight: 600,
                                borderRadius: "10px",
                              }}
                            >
                              {info.title === "Visit Our Office"
                                ? "View on Map"
                                : info.title === "Email Us"
                                  ? "Send Email"
                                  : "Call Now"}
                            </Button>
                          </Box>
                        </Fade>
                      )}
                    </CardContent>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Contact Form Section - WITH LOCATION FIELD ADDED */}
        <Container
          maxWidth="xl"
          sx={{ my: 6, position: "relative", zIndex: 2 }}
          id="contact-form"
        >
          <Grid container spacing={6} alignItems="flex-start">
            {/* Contact Form */}
            <Grid item xs={12} lg={7}>
              <Zoom in timeout={500}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: "28px",
                    position: "relative",
                    overflow: "hidden",
                    background: colors.pureWhite,
                    border: `1px solid ${alpha(colors.charcoalLight, 0.1)}`,
                    boxShadow: `0 25px 50px ${alpha(
                      colors.charcoalGrey,
                      0.08,
                    )}`,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: "6px",
                      background: `linear-gradient(90deg, ${colors.terracotta}, ${colors.softOlive}, ${colors.accentBlue})`,
                      backgroundSize: "300% 100%",
                      animation: "gradientShift 2s ease infinite",
                    },
                  }}
                >
                  <Box sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                    <Box sx={{ mb: 5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 3,
                        }}
                      >
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: "16px",
                            bgcolor: alpha(colors.terracotta, 0.4),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: colors.terracotta,
                            animation: "bounce 2s ease-in-out infinite",
                          }}
                        >
                          <Chat fontSize="large" />
                        </Box>
                        <Box>
                          <Typography
                            variant="h3"
                            sx={{
                              fontFamily: fonts.header,
                              fontWeight: 700,
                              color: colors.charcoalGrey,
                              fontSize: { xs: "1.75rem", md: "2.25rem" },
                            }}
                          >
                            Enquiry Contact Form
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.charcoalLight,
                              fontWeight: 500,
                              mt: 0.5,
                              fontFamily: fonts.body,
                            }}
                          >
                            For strategic inquiries and enterprise partnerships
                          </Typography>
                        </Box>
                      </Box>

                      <Divider
                        sx={{
                          mb: 4,
                          borderColor: alpha(colors.charcoalLight, 0.1),
                        }}
                      />
                    </Box>
                    <Box
                      component="form"
                      onSubmit={handleSubmit}
                      noValidate
                      sx={{ width: "100%" }}
                    >
                      <Grid container spacing={3} sx={{ width: "100%" }}>
                        {/* Personal Information */}
                        <Grid item xs={12}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: colors.charcoalGrey,
                              fontWeight: 600,
                              mb: 2,
                              fontFamily: fonts.body,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Verified
                              sx={{ fontSize: 18, color: colors.softOlive }}
                            />
                            Personal Information
                          </Typography>
                        </Grid>

                        {/* Name Field */}
                        <Grid item xs={12}>
                          <Box sx={{ width: { xs: "100%", sm: "300px" } }}>
                            <TextField
                              fullWidth
                              label="Full Name *"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              error={!!errors.name}
                              helperText={errors.name}
                              required
                              variant="outlined"
                              InputProps={{
                                sx: {
                                  borderRadius: "12px",
                                  fontFamily: fonts.body,
                                  fontSize: "1rem",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: alpha(
                                      colors.charcoalLight,
                                      0.2,
                                    ),
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: colors.terracotta,
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: colors.terracotta,
                                      borderWidth: "2px",
                                    },
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontFamily: fonts.body,
                                  color: alpha(colors.charcoalGrey, 0.7),
                                },
                              }}
                            />
                          </Box>
                        </Grid>

                        {/* Company Field */}
                        <Grid item xs={12} md={6} sm={6}>
                          <Box sx={{ width: { xs: "100%", sm: "300px" } }}>
                            <TextField
                              fullWidth
                              label="Company / Organization"
                              name="company"
                              value={formData.company}
                              onChange={handleChange}
                              variant="outlined"
                              InputProps={{
                                sx: {
                                  borderRadius: "12px",
                                  fontFamily: fonts.body,
                                  fontSize: "1rem",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: alpha(
                                      colors.charcoalLight,
                                      0.2,
                                    ),
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: colors.terracotta,
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Grid>

                        {/* Email Field */}
                        <Grid item xs={12} md={6}>
                          <Box sx={{ width: { xs: "100%", sm: "300px" } }}>
                            <TextField
                              fullWidth
                              label="Business Email *"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleChange}
                              error={!!errors.email}
                              helperText={errors.email}
                              required
                              variant="outlined"
                              InputProps={{
                                sx: {
                                  borderRadius: "12px",
                                  fontFamily: fonts.body,
                                  fontSize: "1rem",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: alpha(
                                      colors.charcoalLight,
                                      0.2,
                                    ),
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: colors.terracotta,
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Grid>

                        {/* Phone Field */}
                        <Grid item xs={12} md={6}>
                          <Box sx={{ width: { xs: "100%", sm: "300px" } }}>
                            <TextField
                              fullWidth
                              label="Phone Number"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              error={!!errors.phone}
                              helperText={errors.phone}
                              variant="outlined"
                              InputProps={{
                                sx: {
                                  borderRadius: "12px",
                                  fontFamily: fonts.body,
                                  fontSize: "1rem",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: alpha(
                                      colors.charcoalLight,
                                      0.2,
                                    ),
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: colors.terracotta,
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Grid>

                        {/* Project Type Field - Optional */}
                        <Grid item xs={12} md={6}>
                          <Box sx={{ width: { xs: "100%", sm: "380px" } }}>
                            <FormControl fullWidth variant="outlined">
                              <InputLabel>Project Type (Optional)</InputLabel>
                              <Select
                                name="projectType"
                                value={
                                  formData.projectType ||
                                  "Commercial Space Design"
                                } // Default value
                                onChange={handleChange}
                                label="Project Type (Optional)"
                                sx={{
                                  borderRadius: "12px",
                                  fontFamily: fonts.body,
                                  fontSize: "1rem",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: alpha(
                                      colors.charcoalLight,
                                      0.2,
                                    ),
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: colors.terracotta,
                                  },
                                }}
                              >
                                <MenuItem value="Commercial Space Design">
                                  Commercial Space Design
                                </MenuItem>
                                {projectTypes
                                  .filter(
                                    (type) =>
                                      type !== "Commercial Space Design",
                                  ) // Exclude default from list
                                  .map((type) => (
                                    <MenuItem key={type} value={type}>
                                      {type}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          </Box>
                        </Grid>

                        {/* LOCATION FIELD - NEWLY ADDED */}
                        <Grid item xs={12} md={6}>
                          <Box sx={{ width: { xs: "100%", sm: "380px" } }}>
                            <TextField
                              fullWidth
                              label="Address"
                              name="location"
                              value={formData.location}
                              onChange={handleChange}
                              error={!!errors.location}
                              helperText={errors.location}
                              variant="outlined"
                              placeholder="City, State, Country"
                              InputProps={{
                                sx: {
                                  borderRadius: "12px",
                                  fontFamily: fonts.body,
                                  fontSize: "1rem",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: alpha(
                                      colors.charcoalLight,
                                      0.2,
                                    ),
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: colors.terracotta,
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Grid>

                        <Grid item xs={12}>
                          <Box sx={{ width: { xs: "100%", sm: "800px" } }}>
                            <TextField
                              fullWidth
                              label="Detailed Message *"
                              name="message"
                              value={formData.message}
                              onChange={handleChange}
                              error={!!errors.message}
                              helperText={errors.message}
                              multiline
                              required
                              variant="outlined"
                              placeholder="Please provide detailed information about your requirements, timeline, budget, and any specific challenges you're facing..."
                              InputProps={{
                                sx: {
                                  borderRadius: "12px",
                                  fontFamily: fonts.body,
                                  fontSize: "1rem",
                                  height: "80px",
                                  maxHeight: "80px",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: alpha(
                                      colors.charcoalLight,
                                      0.2,
                                    ),
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: colors.terracotta,
                                  },
                                  "& .MuiOutlinedInput-input": {
                                    height: "100% !important",
                                    overflow: "auto !important",
                                    resize: "vertical",
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12}></Grid>

                        {/* Submit Button - INSIDE THE FORM */}
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              mt: 4,
                            }}
                          >
                            <Button
                              type="submit"
                              variant="contained"
                              size="large"
                              disabled={submitting}
                              sx={{
                                py: 2,
                                px: 6,
                                borderRadius: "16px",
                                background: `linear-gradient(135deg, ${colors.terracotta} 0%, ${colors.terracottaDark} 100%)`,
                                color: colors.pureWhite,
                                fontSize: "1.1rem",
                                fontWeight: 700,
                                fontFamily: fonts.body,
                                letterSpacing: "0.5px",
                                boxShadow: `0 12px 30px ${alpha(
                                  colors.terracotta,
                                  0.4,
                                )}`,
                                "&:hover": {
                                  background: `linear-gradient(135deg, ${colors.terracottaDark} 0%, ${colors.terracotta} 100%)`,
                                  transform: "translateY(-3px)",
                                  boxShadow: `0 18px 40px ${alpha(
                                    colors.terracotta,
                                    0.6,
                                  )}`,
                                },
                                "&:disabled": {
                                  background: colors.charcoalLight,
                                },
                                transition: "all 0.3s ease",
                                width: { xs: "100%", sm: "auto" },
                              }}
                            >
                              {submitting ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                  }}
                                >
                                  <CircularProgress size={24} color="inherit" />
                                  <span>Processing Request...</span>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                  }}
                                >
                                  <span>Send us your query</span>
                                  <ArrowForward sx={{ ml: 1 }} />
                                </Box>
                              )}
                            </Button>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              textAlign: "center",
                              mt: 3,
                              color: colors.charcoalLight,
                              fontFamily: fonts.body,
                              fontSize: "0.85rem",
                            }}
                          >
                            <Verified
                              sx={{
                                fontSize: 14,
                                verticalAlign: "middle",
                                mr: 0.5,
                                color: colors.softOlive,
                              }}
                            />
                            All inquiries are reviewed by our executive team
                            within 2 business hours
                          </Typography>
                        </Grid>
                        {/* END OF SUBMIT BUTTON */}
                      </Grid>
                    </Box>
                    {/* FORM ENDS HERE */}
                  </Box>
                </Paper>
              </Zoom>
            </Grid>
            <Box sx={{ width: "100%", py: { xs: 4, md: 6 } }}>
              {/* HEADING */}
              <Typography
                variant="h4"
                sx={{
                  fontFamily: fonts.header,
                  fontWeight: 700,
                  color: colors.charcoalGrey,
                  mb: { xs: 3, md: 4 },
                  textAlign: "center",
                  fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                }}
              >
                Contact Information
              </Typography>

              {/* CARDS */}
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                {executiveContactInfo.map((info, index) => (
                  <Grid item xs={12} sm={6} lg={4} key={index}>
                    <Fade
                      in
                      timeout={800}
                      style={{ transitionDelay: `${info.delay}ms` }}
                    >
                      <Box sx={{ height: "100%" }}>
                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: "20px",
                            background: colors.pureWhite,
                            border: `1px solid ${alpha(info.color, 0.1)}`,
                            boxShadow: `0 16px 32px ${alpha(colors.charcoalGrey, 0.08)}`,
                            height: "100%",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-4px)",
                              boxShadow: `0 24px 48px ${alpha(colors.charcoalGrey, 0.12)}`,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              p: { xs: 2, sm: 3 },
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            {/* HEADER */}
                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                mb: 2,
                                alignItems: "center",
                                flexDirection: { xs: "column", sm: "row" },
                                textAlign: { xs: "center", sm: "left" },
                              }}
                            >
                              <Box
                                sx={{
                                  width: { xs: 52, sm: 60 },
                                  height: { xs: 52, sm: 60 },
                                  borderRadius: "14px",
                                  bgcolor: info.bgColor,
                                  color: info.color,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                                }}
                              >
                                {info.icon}
                              </Box>

                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    color: alpha(info.color, 0.8),
                                    fontWeight: 600,
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  {info.subtitle}
                                </Typography>
                                <Typography
                                  variant="h5"
                                  sx={{
                                    color: colors.charcoalGrey,
                                    fontWeight: 700,
                                    fontSize: { xs: "1.1rem", sm: "1.25rem" },
                                  }}
                                >
                                  {info.title}
                                </Typography>
                              </Box>
                            </Box>

                            {/* DETAILS */}
                            <Stack spacing={1.2} sx={{ flex: 1 }}>
                              {info.details.map((detail, idx) => (
                                <Box key={idx} sx={{ display: "flex", gap: 1 }}>
                                  <Typography sx={{ color: info.color }}>
                                    •
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: colors.charcoalLight,
                                      fontWeight: 500,
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    {detail}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>

                            {/* ACTION */}
                            {info.action ? (
                              <Button
                                variant="outlined"
                                fullWidth
                                onClick={info.action}
                                startIcon={
                                  info.title === "Corporate Headquarters" ? (
                                    <LocationOn />
                                  ) : (
                                    <Phone />
                                  )
                                }
                                sx={{
                                  mt: 3,
                                  py: { xs: 1.1, sm: 1 },
                                  fontSize: "0.9rem",
                                  color: info.color,
                                  borderColor: alpha(info.color, 0.3),
                                  fontWeight: 600,
                                  borderRadius: "12px",
                                  "&:hover": {
                                    bgcolor: alpha(info.color, 0.1),
                                    borderColor: info.color,
                                  },
                                }}
                              >
                                {info.title === "Corporate Headquarters"
                                  ? "View on Map"
                                  : "Emergency Call"}
                              </Button>
                            ) : (
                              <Box
                                sx={{
                                  mt: 3,
                                  pt: 2,
                                  borderTop: `1px solid ${alpha(info.color, 0.1)}`,
                                  textAlign: "center",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: alpha(info.color, 0.7),
                                    fontWeight: 600,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Verified sx={{ fontSize: "1rem" }} />
                                  Secure & Encrypted
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Paper>
                      </Box>
                    </Fade>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Container>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          TransitionComponent={Fade}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity="success"
            icon={<CheckCircle />}
            sx={{
              borderRadius: "16px",
              bgcolor: colors.softOlive,
              color: colors.charcoalGrey,
              boxShadow: `0 15px 35px ${alpha(colors.softOlive, 0.3)}`,
              border: `1px solid ${alpha(colors.softOliveDark, 0.2)}`,
              "& .MuiAlert-icon": {
                color: colors.charcoalGrey,
              },
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontFamily: fonts.body }}
            >
              Message Received Successfully!
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: fonts.body }}>
              An executive team member will contact you within 2 hours. Thank
              you for choosing DECODIA.
            </Typography>
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default Contact;
